import { LoadingButton } from '@mui/lab';
import { Alert, Stack, TextField } from '@mui/material';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import * as Yup from 'yup';

import { resetPassword } from 'external/firebase/controllers/auth';
import { getUserByEmail } from 'external/firebase/firestore/collections/users';

import useLocales from 'hooks/useLocales';

interface ResetPasswordRequestFormValues {
    email: string;
}

interface ResetPasswordRequestFormProps {
    onSent: () => void;
    onGetEmail: (value: string) => void;
}

const ResetPasswordRequestForm: React.FC<ResetPasswordRequestFormProps> = ({ onSent, onGetEmail }) => {
    const { translate } = useLocales();
    const { enqueueSnackbar } = useSnackbar();

    const ResetPasswordRequestSchema = Yup.object().shape({
        email: Yup.string()
            .email(translate('email_address_must_be_valid'))
            .required(translate('email_address_required')),
    });

    const formik = useFormik({
        initialValues: {
            email: '',
        },
        validationSchema: ResetPasswordRequestSchema,
        onSubmit: async (
            values: ResetPasswordRequestFormValues,
            { setSubmitting, setStatus, resetForm }: FormikHelpers<ResetPasswordRequestFormValues>,
        ) => {
            try {
                const userResult = await getUserByEmail(values?.email);
                const res = await resetPassword(values.email, userResult);

                setSubmitting(false);
                if (!res.ok) {
                    resetForm();
                    setStatus(translate(res.code));
                    return;
                }
                onSent();
                onGetEmail(res.email || values.email);
            } catch (error) {
                setSubmitting(false);
                resetForm();
                setStatus('An error occurred while processing your request');
            }
        },
    });

    const { errors, status, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

    return (
        <FormikProvider value={formik}>
            <Form autoComplete='off' noValidate onSubmit={handleSubmit}>
                <Stack spacing={3}>
                    {status && <Alert severity='error'>{status}</Alert>}

                    <TextField
                        fullWidth
                        {...getFieldProps('email')}
                        type='email'
                        label={translate('email_address')}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                    />

                    <LoadingButton fullWidth size='large' type='submit' variant='contained' loading={isSubmitting}>
                        {translate('reset_password')}
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default ResetPasswordRequestForm;
